import { constructMSAImageUrl } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';
import { createCurrencyObj } from 'helpers/DataFormatUtils';

const getCardData = props => {
  const {
    alt = '',
    colorId,
    hearts,
    index,
    imageId,
    imageMap = {},
    mainImage = {},
    isHovered,
    msaImageId,
    msaImageParams,
    originalPrice,
    price = null,
    productId,
    productRating,
    reviewCount,
    reviewRating,
    styleId,
    thumbnailImageId,
    fullSizeImage = false,
    imageNoBackground = false,
    animationTimerIndex,
    animationImages,
    showRatingStars
  } = props;

  /**
   * Media props
   */

  const media = {
    imageIndex: index,
    isHovered: false,
    fullSizeImage,
    imageNoBackground,
    mainImage
  };

  const msaId = imageId || thumbnailImageId || msaImageId || null;

  if (msaId) {
    const makeProductHoverImage = () => {
      const { PT01, PT03, TOPP, LEFT, RGHT, BACK } = imageMap;
      return RGHT || TOPP || LEFT || BACK || PT03 || PT01 || msaId;
    };

    const mainImage = msaId;
    const hoverImageId = makeProductHoverImage();
    const hasHoverImage = hoverImageId && mainImage !== hoverImageId;

    media.isHovered = isHovered;

    const imageParams = { ...msaImageParams, width: 540, height: 625 };
    const imageHiResParams = { ...msaImageParams, width: 768, height: 1024 };

    if (animationImages?.length > 0) {
      let activeIndex;
      // If the image has less animation frames than the timer count, just show the last one
      if (animationTimerIndex > animationImages.length - 1) {
        activeIndex = animationImages.length - 1;
      } else {
        activeIndex = animationTimerIndex;
      }

      const { msaId, attrs } = animationImages[activeIndex];

      // this doesn't get a srcSet or else animation doesn't happen
      media.mainImage = {
        src: `https://m.media-amazon.com/images/I/${msaId}._${attrs}_AC_SR255,340.jpg`
      };
    } else {
      const mainImageUrl = constructMSAImageUrl(mainImage, imageParams);
      const imageHiRes = constructMSAImageUrl(mainImage, imageHiResParams);

      const webpMainImageUrl = constructMSAImageUrl(mainImage, {
        ...imageParams,
        useWebp: true
      });
      const webpImageHiRes = constructMSAImageUrl(mainImage, {
        ...imageHiResParams,
        useWebp: true
      });

      media.mainImage = {
        alt: alt,
        src: mainImageUrl,
        srcSet: `${mainImageUrl} 1x, ${imageHiRes} 2x`,
        webp: {
          src: webpMainImageUrl,
          srcSet: `${webpMainImageUrl} 1x, ${webpImageHiRes} 2x`
        }
      };
    }

    if (hasHoverImage) {
      const hoverImageUrl = constructMSAImageUrl(hoverImageId, imageParams);
      const hoverImageHiRes = constructMSAImageUrl(hoverImageId, imageHiResParams);

      const webpHoverImageUrl = constructMSAImageUrl(hoverImageId, {
        ...imageParams,
        useWebp: true
      });
      const webpHoverImageHiRes = constructMSAImageUrl(hoverImageId, {
        ...imageHiResParams,
        useWebp: true
      });

      media.hoverImage = {
        src: hoverImageUrl,
        srcSet: `${hoverImageUrl} 1x, ${hoverImageHiRes} 2x`,
        webp: {
          src: webpHoverImageUrl,
          srcSet: `${webpHoverImageUrl} 1x, ${webpHoverImageHiRes} 2x`
        }
      };
    }
  }

  /**
   * Price component props
   */

  const priceData = {};

  const priceObj = createCurrencyObj(price);

  if (price) {
    priceData.price = priceObj;
    priceData.label = priceData.price.string;
  }

  if (priceObj.string && originalPrice) {
    priceData.msrp = createCurrencyObj(originalPrice);
    priceData.onSale = ProductUtils.isStyleOnSale({
      price: priceObj.string,
      originalPrice: priceData.msrp.string
    });
    if (priceData.onSale) {
      priceData.label = `On sale for ${priceData.price.string}. MSRP ${priceData.msrp.string}.`;
    }
  }

  /**
   * Review component props
   */

  const reviews = {};

  if ((showRatingStars && productRating) || reviewRating) {
    const rating = typeof reviewRating === 'number' ? reviewRating : parseInt(productRating, 10);
    const floatRating = rating.toFixed(1);
    reviews.roundedRating = productRating;
    reviews.rating = floatRating;
    reviews.label = floatRating > 0 ? `${floatRating} out of 5 stars` : '';
  }

  if (reviewCount) {
    reviews.ratingCount = reviewCount;
  }

  /**
   * Hearts component props
   */
  let heartProps = {};

  if (hearts) {
    heartProps = {
      ...hearts,
      styleId,
      productId,
      colorId,
      price: priceData?.price?.string
    };
  }

  return { hearts: heartProps, media, price: priceData, reviews };
};

export default getCardData;
